import { useRef } from 'react';
import { Voucher, VoucherStatus, voucherTypeColors, voucherTypeIcons } from '../../../mocks/vouchers';
import { agencyInfo } from '../../../mocks/dashboard';

const STATUS_STYLES: Record<VoucherStatus, string> = {
  Active: 'bg-emerald-100 text-emerald-700',
  Used: 'bg-gray-100 text-gray-500',
  Cancelled: 'bg-red-100 text-red-600',
  Expired: 'bg-orange-100 text-orange-600',
};

interface VoucherModalProps {
  voucher: Voucher;
  onClose: () => void;
}

export default function VoucherModal({ voucher, onClose }: VoucherModalProps) {
  const printRef = useRef<HTMLDivElement>(null);
  const brand = agencyInfo.brandColor;
  const typeColor = voucherTypeColors[voucher.type];
  const typeIcon = voucherTypeIcons[voucher.type];

  const fmtDate = (d: string) =>
    new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });

  const handlePrint = () => {
    const printContents = printRef.current?.innerHTML;
    if (!printContents) return;
    const w = window.open('', '', 'width=800,height=700');
    if (!w) return;
    w.document.write(`
      <html><head><title>${voucher.number}</title>
      <style>
        body { font-family: 'Helvetica Neue', Arial, sans-serif; margin: 0; padding: 0; color: #1a1a1a; }
        * { box-sizing: border-box; }
        .print-section { page-break-inside: avoid; }
        .voucher-header { padding: 32px 40px; }
        .voucher-body { padding: 24px 40px; }
        .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
        .label { font-size: 10px; color: #999; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 4px; font-weight: 600; }
        .value { font-size: 13px; color: #222; font-weight: 600; }
        .detail-item { padding: 12px 0; border-bottom: 1px solid #f0f0f0; }
        @media print { body { padding: 0; } }
      </style>
      </head><body>${printContents}</body></html>
    `);
    w.document.close();
    w.focus();
    w.print();
    w.close();
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white w-full max-w-2xl rounded-2xl overflow-hidden max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-xl" style={{ backgroundColor: `${typeColor}15` }}>
              <i className={`${typeIcon} text-lg`} style={{ color: typeColor }} />
            </div>
            <div>
              <h2 className="font-bold text-gray-900">{voucher.number}</h2>
              <p className="text-xs text-gray-400">{voucher.type} Voucher · {voucher.customer}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${STATUS_STYLES[voucher.status]}`}>
              {voucher.status}
            </span>
            <button
              onClick={handlePrint}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-white text-xs font-medium cursor-pointer"
              style={{ backgroundColor: brand }}
            >
              <i className="ri-printer-line" />
              Print
            </button>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer">
              <i className="ri-close-line text-lg" />
            </button>
          </div>
        </div>

        {/* Printable Voucher Content */}
        <div className="flex-1 overflow-y-auto">
          <div ref={printRef}>
            {/* Voucher Top Bar */}
            <div className="px-8 py-6" style={{ backgroundColor: typeColor }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center text-white font-bold text-lg">
                    {agencyInfo.logo}
                  </div>
                  <div>
                    <p className="font-bold text-white text-lg">{agencyInfo.name}</p>
                    <p className="text-white/80 text-xs">Service Voucher</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-white/70 text-xs uppercase tracking-wider font-medium">{voucher.type} Voucher</p>
                  <p className="text-white font-bold text-xl mt-1">{voucher.number}</p>
                  <p className="text-white/70 text-xs mt-1">Issued: {fmtDate(voucher.issuedDate)}</p>
                </div>
              </div>
            </div>

            {/* Service Title Banner */}
            <div className="px-8 py-4" style={{ backgroundColor: `${typeColor}15` }}>
              <div className="flex items-center gap-3">
                <i className={`${typeIcon} text-2xl`} style={{ color: typeColor }} />
                <div>
                  <p className="font-bold text-gray-900 text-lg">{voucher.serviceTitle}</p>
                  <p className="text-sm text-gray-500">{voucher.destination}</p>
                </div>
              </div>
            </div>

            <div className="px-8 py-6 space-y-6">
              {/* Guest Information */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-xs text-gray-400 uppercase font-medium tracking-wider mb-3">Guest Information</p>
                  <div className="space-y-2 text-sm">
                    <div>
                      <p className="text-xs text-gray-400">Name</p>
                      <p className="font-semibold text-gray-800">{voucher.customer}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">Contact</p>
                      <p className="text-gray-700">{voucher.customerPhone}</p>
                      <p className="text-gray-700">{voucher.customerEmail}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">Passengers</p>
                      <p className="font-semibold text-gray-800">{voucher.pax} {voucher.pax === 1 ? 'Person' : 'Persons'}</p>
                    </div>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase font-medium tracking-wider mb-3">Travel Dates</p>
                  <div className="space-y-2 text-sm">
                    <div>
                      <p className="text-xs text-gray-400">Travel Date</p>
                      <p className="font-semibold text-gray-800">{fmtDate(voucher.travelDate)}</p>
                    </div>
                    {voucher.travelDate !== voucher.endDate && (
                      <div>
                        <p className="text-xs text-gray-400">Return / End Date</p>
                        <p className="font-semibold text-gray-800">{fmtDate(voucher.endDate)}</p>
                      </div>
                    )}
                    {voucher.bookingId && (
                      <div>
                        <p className="text-xs text-gray-400">Booking Reference</p>
                        <p className="font-semibold text-gray-800">{voucher.bookingId}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Type-specific Details */}
              {(voucher.checkIn || voucher.roomType) && (
                <div className="border border-gray-100 rounded-xl p-5">
                  <p className="text-xs text-gray-400 uppercase font-medium tracking-wider mb-4">Hotel Details</p>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {voucher.checkIn && (
                      <div>
                        <p className="text-xs text-gray-400">Check-in</p>
                        <p className="font-semibold text-gray-800">{fmtDate(voucher.checkIn)}</p>
                      </div>
                    )}
                    {voucher.checkOut && (
                      <div>
                        <p className="text-xs text-gray-400">Check-out</p>
                        <p className="font-semibold text-gray-800">{fmtDate(voucher.checkOut)}</p>
                      </div>
                    )}
                    {voucher.roomType && (
                      <div className="col-span-2">
                        <p className="text-xs text-gray-400">Room Type</p>
                        <p className="font-semibold text-gray-800">{voucher.roomType}</p>
                      </div>
                    )}
                    {voucher.mealPlan && (
                      <div>
                        <p className="text-xs text-gray-400">Meal Plan</p>
                        <p className="font-semibold text-gray-800">{voucher.mealPlan}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {(voucher.pickupLocation || voucher.vehicleType) && (
                <div className="border border-gray-100 rounded-xl p-5">
                  <p className="text-xs text-gray-400 uppercase font-medium tracking-wider mb-4">Transfer Details</p>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {voucher.pickupLocation && (
                      <div>
                        <p className="text-xs text-gray-400">Pickup</p>
                        <p className="font-semibold text-gray-800">{voucher.pickupLocation}</p>
                      </div>
                    )}
                    {voucher.dropoffLocation && (
                      <div>
                        <p className="text-xs text-gray-400">Drop-off</p>
                        <p className="font-semibold text-gray-800">{voucher.dropoffLocation}</p>
                      </div>
                    )}
                    {voucher.pickupTime && (
                      <div>
                        <p className="text-xs text-gray-400">Pickup Time</p>
                        <p className="font-semibold text-gray-800">{voucher.pickupTime}</p>
                      </div>
                    )}
                    {voucher.vehicleType && (
                      <div>
                        <p className="text-xs text-gray-400">Vehicle</p>
                        <p className="font-semibold text-gray-800">{voucher.vehicleType}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {(voucher.tourName || voucher.meetingPoint) && (
                <div className="border border-gray-100 rounded-xl p-5">
                  <p className="text-xs text-gray-400 uppercase font-medium tracking-wider mb-4">Tour Details</p>
                  <div className="space-y-3 text-sm">
                    {voucher.tourName && (
                      <div>
                        <p className="text-xs text-gray-400">Tour Name</p>
                        <p className="font-semibold text-gray-800">{voucher.tourName}</p>
                      </div>
                    )}
                    {voucher.tourGuide && (
                      <div>
                        <p className="text-xs text-gray-400">Tour Guide</p>
                        <p className="font-semibold text-gray-800">{voucher.tourGuide}</p>
                      </div>
                    )}
                    {voucher.meetingPoint && (
                      <div>
                        <p className="text-xs text-gray-400">Meeting Point</p>
                        <p className="font-semibold text-gray-800">{voucher.meetingPoint}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Services Included */}
              <div>
                <p className="text-xs text-gray-400 uppercase font-medium tracking-wider mb-3">Services Included</p>
                <div className="space-y-2">
                  {voucher.serviceDetails.map((detail, idx) => (
                    <div key={idx} className="flex items-start gap-2.5 text-sm text-gray-700">
                      <div className="w-4 h-4 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5" style={{ backgroundColor: `${typeColor}20` }}>
                        <i className="ri-check-line text-xs" style={{ color: typeColor }} />
                      </div>
                      <span>{detail}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Supplier */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-xs text-gray-400 uppercase font-medium tracking-wider mb-3">Supplier</p>
                  <div className="space-y-1 text-sm">
                    <p className="font-semibold text-gray-800">{voucher.supplierName}</p>
                    <p className="text-gray-500 text-xs">{voucher.supplierAddress}</p>
                    <p className="text-gray-500 text-xs">{voucher.supplierPhone}</p>
                    <p className="text-gray-500 text-xs">{voucher.supplierEmail}</p>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase font-medium tracking-wider mb-3">Confirmation</p>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs text-gray-400 mb-1">Supplier Reference</p>
                    <p className="font-semibold text-gray-700 text-sm">{voucher.supplierRef}</p>
                    <p className="text-xs text-gray-400 mt-3 mb-1">Confirmation Code</p>
                    <p className="font-bold text-base font-mono" style={{ color: typeColor }}>{voucher.confirmationCode}</p>
                  </div>
                </div>
              </div>

              {/* Special Instructions */}
              {voucher.specialInstructions && (
                <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
                  <p className="text-xs font-semibold text-amber-600 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <i className="ri-information-line" />
                    Special Instructions
                  </p>
                  <p className="text-sm text-amber-800 leading-relaxed">{voucher.specialInstructions}</p>
                </div>
              )}

              {/* Footer */}
              <div className="border-t border-gray-100 pt-4 text-center">
                <p className="text-xs text-gray-400">
                  This voucher is issued by <strong>{agencyInfo.name}</strong> on behalf of {voucher.customer}.
                  Please present this voucher at the time of service.
                </p>
                <p className="text-xs text-gray-300 mt-1">Issued: {fmtDate(voucher.issuedDate)} · Agent: {voucher.agent}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
